// Shared configuration for markdown compilation and the "view/copy as markdown"
// buttons. Both scripts/compile-docs-to-markdown.js and the React components
// consume these rules so they stay in sync.
//
// Each rule excludes paths that contain `pattern` (substring match). An
// optional `except` lists substrings that skip the rule when the path also
// contains one (used so GB/EU `/resources/` and `/blog/` pages still get
// markdown / llms.txt). An
// optional `scope` restricts where the rule applies; rules with no scope
// apply to every path. The only scoped value today is `non-docs`, used for
// marketing-team test fixtures — /docs/ pages gate themselves via the
// skip-llm meta tag and should not be filtered by those patterns.

const MARKDOWN_EXCLUDE_RULES = {
  main: [
    { pattern: '/accelerate/' },
    { pattern: '/careers/' },
    { pattern: '/cra/' },
    { pattern: '/link-demo/' },
    { pattern: '/en-eu/', except: ['/resources/', '/blog/'] },
    { pattern: '/en-gb/', except: ['/resources/', '/blog/'] },
    { pattern: '/en/' },
    { pattern: '/es/' },
    { pattern: '/fr/' },
    { pattern: '/out/institutions/' },
    { pattern: '/tutorials/' },
    { pattern: '/out/team-update/index.html' },
    { pattern: '404' },
    { pattern: '500' },
    { pattern: '/blog/page/' },
    { pattern: '/blog/tag/' },
    { pattern: '/core-exchange/' },
    { pattern: '/plaid-exchange/' },
    { pattern: '/wp/' },
    { pattern: 'placeholder' },
    { pattern: 'staging' },
    { pattern: '/test-', scope: 'non-docs' },
    { pattern: '-test/', scope: 'non-docs' },
    { pattern: '/2025-fp-whitepaper/' },
  ],
  coreExchange: [
    { pattern: '/reference/4.6/' },
    { pattern: '/reference/5.0/' },
    { pattern: '/reference/5.1/' },
    { pattern: '/reference/5.2/' },
    { pattern: '/reference/5.3/' },
    { pattern: '/reference/6.0/' },
    { pattern: '/reference/6.1/' },
    { pattern: '/reference/6.2/' },
  ],
};

const ruleApplies = (rule, isDocsPath) => {
  if (rule.scope === undefined) return true;
  if (rule.scope === 'non-docs') return !isDocsPath;
  throw new Error(`Unknown rule scope: ${rule.scope}`);
};

const isPathExcluded = (path, rules) => {
  // Normalize to include trailing slash so patterns like '/reference/5.1/' match
  const normalized = path.endsWith('/') ? path : path + '/';
  // Matches both /docs/ and /core-exchange/docs/ — both have their own
  // skip-llm gating and should bypass non-docs-scoped rules.
  const isDocsPath = normalized.includes('/docs/');
  return rules.some((rule) => {
    if (!ruleApplies(rule, isDocsPath) || !normalized.includes(rule.pattern)) {
      return false;
    }
    const isExcepted = rule.except?.some((except) => {
      return normalized.includes(except);
    });
    if (isExcepted) {
      return false;
    }
    return true;
  });
};

/**
 * Returns true if the given URL path should be excluded from markdown.
 * Applies each config's excludes only to paths belonging to that config:
 *   /core-exchange/docs/* → checked against coreExchange rules
 *   /docs/* (and other paths) → checked against main rules
 */
const isMarkdownExcluded = (path) => {
  const rules = path.startsWith('/core-exchange/docs/')
    ? MARKDOWN_EXCLUDE_RULES.coreExchange
    : MARKDOWN_EXCLUDE_RULES.main;
  return isPathExcluded(path, rules);
};

module.exports = {
  MARKDOWN_EXCLUDE_RULES,
  isPathExcluded,
  isMarkdownExcluded,
};
